import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BrandWordmark } from "./BrandWordmark";
import { NEWS_PATH } from "@/lib/news";

const BrandMark = ({ className = "" }) => (
  <span
    translate="no"
    className={`notranslate inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm ${className}`}
  >
    <BrandWordmark />
  </span>
);

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const MENU_ITEMS = [
    ["realisiert", t.nav.realisiert],
    ["anwendungen", t.nav.anwendungen],
    ["challenges", t.nav.challenges],
    ["collection", t.nav.collection],
    ["impact", t.nav.impact],
    ["vision-berlin", t.nav.berlin],
    ["tech", t.nav.tech],
    ["financing", t.nav.finance],
    ["presse", t.nav.presse],
    [NEWS_PATH[lang], t.nav.aktuelles],
    ["contact", t.nav.contact],
  ];

  const hrefFor = (id) => (id.startsWith("/") ? id : `#${id}`);
  const idFor = (id) => (id.startsWith("/") ? "aktuelles" : id);

  const link = (id, label) => (
    <a
      key={id}
      href={hrefFor(id)}
      className="hairline text-[10px] tracking-[0.12em] uppercase text-white/70 hover:text-white transition-colors whitespace-nowrap"
      data-testid={`nav-link-${idFor(id)}`}
    >
      {label}
    </a>
  );

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled ? "bg-[#1F2328]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="brand-logo">
          <BrandMark />
        </a>

        <nav
          className={`hidden 2xl:flex items-center gap-4 transition-opacity duration-700 ${
            scrolled ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {link("realisiert", t.nav.realisiert)}
          {link("challenges", t.nav.challenges)}
          {link("collection", t.nav.collection)}
          {link("impact", t.nav.impact)}
          {link("vision-berlin", t.nav.berlin)}
          {link("tech", t.nav.tech)}
          {link("financing", t.nav.finance)}
          {link("presse", t.nav.presse)}
          {link(NEWS_PATH[lang], t.nav.aktuelles)}
          {link("contact", t.nav.contact)}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className={`hidden md:flex items-center border border-white/10 rounded-full overflow-hidden text-[10px] tracking-[0.22em] transition-opacity duration-700 ${
              scrolled ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang("de")}
              className={`px-3 py-1.5 transition ${lang === "de" ? "bg-white text-[#1F2328]" : "text-white/60 hover:text-white"}`}
              data-testid="lang-de"
            >
              DE
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition ${lang === "en" ? "bg-white text-[#1F2328]" : "text-white/60 hover:text-white"}`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          <a
            href="#contact"
            data-testid="nav-cta-btn"
            className={`hidden md:inline-flex items-center whitespace-nowrap rounded-full bg-[#F5D77A] hover:bg-white text-[#1F2328] text-[11px] tracking-[0.18em] font-medium px-4 h-9 transition-all duration-700 ${
              scrolled ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            {t.nav.cta}
          </a>

          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-pdf-btn"
            className={`hidden md:inline-flex items-center gap-2 border border-white/25 text-white text-[10px] tracking-[0.22em] uppercase px-4 h-9 hover:bg-white hover:text-[#1F2328] transition-all duration-700 rounded-full ${
              scrolled ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            PDF
          </a>

          <button
            onClick={() => setOpen(true)}
            aria-label={lang === "de" ? "Menü öffnen" : "Open menu"}
            data-testid="nav-mobile-menu-btn"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 -mr-2 text-white/85 hover:text-white transition-colors"
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed inset-0 z-50 bg-[#16191D] flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-white/8">
              <BrandMark />
              <button
                onClick={() => setOpen(false)}
                aria-label={lang === "de" ? "Menü schließen" : "Close menu"}
                data-testid="nav-mobile-menu-close"
                className="inline-flex items-center justify-center h-11 w-11 -mr-2 text-white/85 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1">
              {MENU_ITEMS.map(([id, label], i) => (
                <motion.a
                  key={id}
                  href={hrefFor(id)}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.04 }}
                  className="py-3 font-display font-light text-2xl tracking-[-0.01em] text-white/85 hover:text-[#F5D77A] transition-colors border-b border-white/6"
                  data-testid={`nav-mobile-link-${idFor(id)}`}
                >
                  {label}
                </motion.a>
              ))}

              <div className="mt-8 flex items-center gap-4">
                <div
                  className="flex items-center border border-white/15 rounded-full overflow-hidden text-[11px] tracking-[0.22em]"
                  data-testid="nav-mobile-lang-toggle"
                >
                  <button
                    onClick={() => { setLang("de"); setOpen(false); }}
                    className={`px-4 py-2.5 transition ${lang === "de" ? "bg-white text-[#1F2328]" : "text-white/60"}`}
                    data-testid="nav-mobile-lang-de"
                  >
                    DE
                  </button>
                  <button
                    onClick={() => { setLang("en"); setOpen(false); }}
                    className={`px-4 py-2.5 transition ${lang === "en" ? "bg-white text-[#1F2328]" : "text-white/60"}`}
                    data-testid="nav-mobile-lang-en"
                  >
                    EN
                  </button>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-pdf-btn"
                  className="inline-flex items-center gap-2 border border-white/25 text-white text-[11px] tracking-[0.22em] uppercase px-5 h-11 rounded-full"
                >
                  PDF
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              </div>
            </nav>

            <div className="px-6 pb-8 pt-4 border-t border-white/8">
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-cta"
                className="flex items-center justify-center w-full rounded-full bg-[#F5D77A] text-[#1F2328] text-[12px] tracking-[0.18em] font-medium h-12"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
